import { useToast } from "vue-toastification";
import { lockAchHiddenInputs } from "@/utils/achElement.js";

const toast = useToast();

export default {
  data() {
    return {
      achElements: null,
      achPaymentElement: null,
      achClientSecret: "",
      achIntentAmount: "",
      achError: "",
      achLoading: false,
      achFormComplete: false,
      achTabUnlock: null,
      achInitPromise: null,
      achPaying: false,
      achStartedMessage:
        "ACH payment started. Your order is on hold until the bank transfer clears.",
    };
  },
  computed: {
    achBillingDetails() {
      return {
        name: `${this.form?.first_name || ""} ${this.form?.last_name || ""}`.trim(),
        email: String(this.form?.email || "").trim(),
      };
    },
  },
  watch: {
    "form.first_name"() {
      this.syncAchBillingDetails();
    },
    "form.last_name"() {
      this.syncAchBillingDetails();
    },
    "form.email"() {
      this.syncAchBillingDetails();
    },
    selectedPaymentMethod(value) {
      if (value === "ach") this.prefillAchAccountHolder();
    },
    paymentOption(value) {
      if (value === "ach") this.prefillAchAccountHolder();
    },
    method(value) {
      if (value === "ach") this.prefillAchAccountHolder();
    },
  },
  mounted() {
    this.prefillAchAccountHolder();
  },
  methods: {
    splitPersonName(value) {
      const name = String(value || "").trim().replace(/\s+/g, " ");
      if (!name) return { first: "", last: "" };
      const space = name.indexOf(" ");
      if (space === -1) return { first: name, last: "" };
      return {
        first: name.slice(0, space),
        last: name.slice(space + 1).trim(),
      };
    },
    prefillAchAccountHolder() {
      if (!this.form) return;
      const user = this.$store?.getters?.["Auth/getUser"] || {};
      if (!String(this.form.email || "").trim() && user.email) {
        this.form.email = user.email;
      }
      const parts = this.splitPersonName(user.name);
      if (!String(this.form.first_name || "").trim() && parts.first) {
        this.form.first_name = parts.first;
      }
      if (!String(this.form.last_name || "").trim() && parts.last) {
        this.form.last_name = parts.last;
      }
    },
    isAchSelected() {
      if (this.selectedPaymentMethod) return this.selectedPaymentMethod === "ach";
      if (this.paymentOption) return this.paymentOption === "ach";
      return this.method === "ach";
    },
    payableForAch() {
      return this.payableTotal;
    },
    achIntentPayload() {
      const amount = Number(this.payableForAch() || 0);
      return {
        amount: amount.toFixed(2),
        email: String(this.form?.email || "").trim(),
        first_name: this.form?.first_name || "",
        last_name: this.form?.last_name || "",
        ach_discount: Number(this.achDiscountAmount) || 0,
        coupon_discount: Number(this.couponDiscountAmount ?? this.discount) || 0,
        discount: Number(this.combinedDiscount ?? this.discount) || 0,
      };
    },
    isValidAchEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
    },
    setAchFieldError(key, message) {
      if (!this.fieldErrors || typeof this.fieldErrors !== "object") {
        this.fieldErrors = {};
      }
      this.fieldErrors[key] = message;
    },
    syncAchBillingDetails() {
      if (!this.achPaymentElement) return;
      try {
        this.achPaymentElement.update({
          defaultValues: {
            billingDetails: this.achBillingDetails,
          },
        });
      } catch {
        // Stripe element may already be unmounted
      }
    },
    friendlyAchError(error) {
      const message = String(error?.message || error || "").trim();
      const code = String(error?.code || "").toLowerCase();
      const lower = message.toLowerCase();
      if (
        lower.includes("confirmparams") ||
        lower.includes("payment_method_data") ||
        lower.includes("fields.billing") ||
        lower.includes("you specified") ||
        lower.includes("opt out of collecting")
      ) {
        return "Please confirm your bank details and try again.";
      }
      if (
        code === "incomplete" ||
        lower.includes("please select a payment method") ||
        lower.includes("canceled") ||
        lower.includes("cancelled")
      ) {
        return "Please confirm your bank details to continue.";
      }
      return message || "ACH payment failed.";
    },
    teardownAchElement() {
      if (typeof this.achTabUnlock === "function") {
        this.achTabUnlock();
      }
      this.achTabUnlock = null;
      if (this.achPaymentElement) {
        try {
          this.achPaymentElement.unmount();
        } catch {
          // already unmounted
        }
        this.achPaymentElement = null;
      }
      this.achElements = null;
      this.achFormComplete = false;
    },
    releaseCardElement() {
      if (!this.stripCard) return;
      try {
        this.stripCard.unmount();
      } catch {
        // already unmounted
      }
      this.stripCard = null;
    },
    async initializeAchElement() {
      if (!this.isAchSelected()) return;
      const amount = Number(this.payableForAch() || 0).toFixed(2);
      if (
        this.achPaymentElement &&
        this.achElements &&
        this.achClientSecret &&
        this.achIntentAmount === amount
      ) {
        return;
      }
      if (this.achInitPromise) return this.achInitPromise;

      this.achInitPromise = this.mountAchElement(amount).finally(() => {
        this.achInitPromise = null;
        const latest = Number(this.payableForAch() || 0).toFixed(2);
        if (this.isAchSelected() && this.achClientSecret && this.achIntentAmount !== latest) {
          this.achClientSecret = "";
          this.teardownAchElement();
          this.initializeAchElement();
        }
      });
      return this.achInitPromise;
    },
    async mountAchElement(amount) {
      this.achError = "";
      this.achLoading = true;
      try {
        if (this.achIntentAmount && this.achIntentAmount !== amount) {
          this.achClientSecret = "";
          this.teardownAchElement();
        }
        if (!this.stripeElement) {
          const StripeConstructor = await this.loadStripeScript();
          this.stripeElement = StripeConstructor(this.stripeKey);
        }
        if (!this.achClientSecret || this.achIntentAmount !== amount) {
          const intent = await this.$store.dispatch(
            "Product/orderAchPaymentIntentAction",
            this.achIntentPayload(),
            { root: true }
          );
          this.achClientSecret = intent?.client_secret || intent?.data?.client_secret || "";
          this.achIntentAmount = amount;
        }
        if (!this.achClientSecret) {
          this.achError = "Unable to start ACH checkout.";
          return;
        }
        await this.$nextTick();
        const host = document.getElementById("order-ach-element");
        if (!host || !this.isAchSelected()) return;

        this.teardownAchElement();
        host.innerHTML = "";
        this.achElements = this.stripeElement.elements({
          clientSecret: this.achClientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              fontFamily: "Inter, sans-serif",
              fontSizeBase: "14px",
              colorPrimary: "#14b8c1",
            },
          },
        });
        this.achPaymentElement = this.achElements.create("payment", {
          layout: "tabs",
          wallets: {
            applePay: "never",
            googlePay: "never",
          },
          defaultValues: {
            billingDetails: this.achBillingDetails,
          },
          fields: {
            billingDetails: {
              name: "never",
              email: "never",
            },
          },
        });
        this.achPaymentElement.mount("#order-ach-element");
        this.achTabUnlock = lockAchHiddenInputs(host);
        this.achPaymentElement.on("ready", () => {
          this.achLoading = false;
          this.syncAchBillingDetails();
        });
        this.achPaymentElement.on("change", (event) => {
          this.achFormComplete = !!event?.complete;
          if (event?.error) {
            this.achError = this.friendlyAchError(event.error);
          } else if (this.achFormComplete) {
            this.achError = "";
          }
        });
        this.achPaymentElement.on("loaderror", (event) => {
          this.achLoading = false;
          this.achError = this.friendlyAchError(event?.error) || "Unable to load bank checkout.";
        });
      } catch (e) {
        this.achError =
          this.friendlyAchError(
            e?.response?.data?.message ||
              (e?.response?.data?.errors && Object.values(e.response.data.errors).flat()[0]) ||
              e?.message
          ) || "Unable to load ACH checkout.";
      } finally {
        if (!this.achPaymentElement) this.achLoading = false;
      }
    },
    async remountAchElement() {
      this.achClientSecret = "";
      this.achIntentAmount = "";
      this.teardownAchElement();
      await this.initializeAchElement();
    },
    isAchPaymentConfirmed(paymentIntent) {
      const status = String(paymentIntent?.status || "").toLowerCase();
      if (status !== "processing" && status !== "succeeded") return false;
      if (paymentIntent?.next_action) return false;
      return true;
    },
    async getConfirmedAchIntent(paymentIntent) {
      try {
        if (!this.stripeElement || !this.achClientSecret) return paymentIntent;
        const { paymentIntent: freshIntent } = await this.stripeElement.retrievePaymentIntent(
          this.achClientSecret
        );
        return freshIntent || paymentIntent;
      } catch {
        return paymentIntent;
      }
    },
    async beforeAchConfirm() {
      return true;
    },
    async submitAchPayment() {
      if (this.achPaying || this.paying || this.loading) return;
      this.achPaying = true;
      await this.$nextTick();
      this.achError = "";
      this.setAchFieldError("first_name", "");
      this.setAchFieldError("last_name", "");
      this.setAchFieldError("email", "");

      if (!String(this.form?.first_name || "").trim()) {
        this.setAchFieldError("first_name", "First name is required.");
      }
      if (!String(this.form?.last_name || "").trim()) {
        this.setAchFieldError("last_name", "Last name is required.");
      }
      const achEmail = String(this.form?.email || "").trim();
      if (!achEmail) {
        this.setAchFieldError("email", "Email is required.");
      } else if (!this.isValidAchEmail(achEmail)) {
        this.setAchFieldError("email", "Please enter a valid email.");
      }
      if (this.fieldErrors.first_name || this.fieldErrors.last_name || this.fieldErrors.email) {
        this.achPaying = false;
        toast.error("Please enter the account holder first name, last name, and email.", {
          timeout: 3000,
        });
        return;
      }
      if (!this.achFormComplete) {
        this.achPaying = false;
        this.achError = "Please confirm your bank details to continue.";
        toast.error(this.achError, { timeout: 4000 });
        return;
      }
      if (!(await this.beforeAchConfirm())) {
        this.achPaying = false;
        return;
      }

      this.loading = true;
      try {
        if (!this.achElements || !this.achClientSecret) {
          await this.initializeAchElement();
          if (!this.achElements || !this.achClientSecret) {
            toast.error(this.achError || "ACH form is not ready yet.", { timeout: 4000 });
            return;
          }
        }

        this.syncAchBillingDetails();
        await this.$nextTick();

        const billingDetails = this.achBillingDetails;
        const { error, paymentIntent } = await this.stripeElement.confirmPayment({
          elements: this.achElements,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: {
              billing_details: {
                name: billingDetails.name,
                email: billingDetails.email,
              },
            },
          },
          redirect: "if_required",
        });

        if (error) {
          this.achError = this.friendlyAchError(error);
          toast.error(this.achError, { timeout: 4000 });
          await this.remountAchElement();
          return;
        }

        const confirmedIntent = await this.getConfirmedAchIntent(paymentIntent);
        if (!this.isAchPaymentConfirmed(confirmedIntent)) {
          this.achError = "Please confirm your bank details to continue.";
          toast.error(this.achError, { timeout: 4000 });
          await this.remountAchElement();
          return;
        }

        await this.finalizeAchOrder(confirmedIntent);
      } catch (e) {
        const msg = this.friendlyAchError(
          e?.response?.data?.message ||
            (e?.response?.data?.errors && Object.values(e.response.data.errors).flat()[0]) ||
            e?.message
        );
        this.achError = msg;
        toast.error(msg, { timeout: 4000 });
        await this.remountAchElement();
      } finally {
        this.loading = false;
        this.achPaying = false;
      }
    },
    async handleAchRedirectReturn() {
      const clientSecret = String(this.$route?.query?.payment_intent_client_secret || "").trim();
      if (!clientSecret) return false;

      try {
        if (!this.stripeElement) {
          const StripeConstructor = await this.loadStripeScript();
          this.stripeElement = StripeConstructor(this.stripeKey);
        }
        const { paymentIntent, error } = await this.stripeElement.retrievePaymentIntent(clientSecret);
        if (error || !this.isAchPaymentConfirmed(paymentIntent)) return false;
        this.loading = true;
        await this.finalizeAchOrder(paymentIntent);
        return true;
      } catch {
        return false;
      } finally {
        this.loading = false;
      }
    },
  },
};
